import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui';

export interface MenuItem {
  key: string;
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

export interface MenuGroup {
  title: string;
  items: MenuItem[];
}

/**
 * Bouton « Action sur le billet » + menu déroulant groupé (Statut / Données).
 * Le menu ne fait qu'ouvrir une modale (confirmation ou édition) : rien n'est envoyé au clic.
 * Fermeture au clic extérieur et à Échap.
 */
export default function BilletActionsMenu({ groups, disabled = false }: { groups: MenuGroup[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative inline-block">
      <Button
        size="sm"
        variant="primary"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Action sur le billet
        <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
      </Button>
      {open ? (
        <div
          role="menu"
          aria-label="Action sur le billet"
          className="absolute right-0 z-30 mt-1 w-72 overflow-hidden rounded-lg border border-card-border bg-surface-neutral py-1 shadow-elevation-3"
        >
          {groups.map((group, gi) => (
            <div key={group.title} className={gi > 0 ? 'mt-1 border-t border-card-border pt-1' : ''}>
              <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-outline">
                {group.title}
              </p>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-white/10 focus:bg-white/10 focus:outline-none ${
                    item.danger ? 'text-error-container' : 'text-white'
                  }`}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
